import { HttpService } from '../../../../services/http.service';
import { forkJoin, Observable, switchMap, throwError } from 'rxjs';
import { StorageService } from '../../../../services/storage.service';
import { UserAccountDTO } from '../../../../classes/user-account.dto';
import { DataService } from '../../../../services/data.service';
import { GtmService } from '../../../../services/gtm.service';
import { TranslateModule } from '@ngx-translate/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { CommonModule } from '@angular/common';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { addDays, addYears, subYears } from 'date-fns';
import { AriaAutocompleteFieldComponent } from '../../../shared/aria-autocomplete-field/aria-autocomplete-field.component';
import { AriaFieldOption } from '../../../shared';
import { AriaDatePickerFieldComponent } from '../../../shared/aria-date-picker-field/aria-date-picker-field.component';

@Component({
    selector: 'app-modal-add-new-record',
    imports: [TranslateModule, NzDividerModule, CommonModule, NzGridModule, ReactiveFormsModule, NzCheckboxModule, NzFormModule, AriaAutocompleteFieldComponent, AriaDatePickerFieldComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './modal-add-new-record.component.html',
    styleUrl: './modal-add-new-record.component.scss'
})
export class ModalAddNewRecordComponent {
  isLoading = true;
  modalOptions: { texts: string[]; values: string[] } = { texts: [], values: [] };
  specificAllergenOptions: { texts: string[]; values: string[] } = { texts: [], values: [] };
  selectedOption: string | null = null;
  formModal: FormGroup;
  todayDate = new Date();
  companyId?: string;
  details = {
    gender: '-',
    apparatus: '-',
    organ: '-',
    bodyPart: '-',
    bodyPartKey: '',
  };
  selectedDrugName!: string;
  drugFormats: { texts: string[]; values: string[] } = { texts: [], values: [] };
  httpOptions: any[] = [];
  doseOptions: AriaFieldOption[] = [
    '1',
    '2',
    '3',
    '4',
    '6',
    '8',
    '12',
    '24',
    '48',
    '72',
    ].map((value) => ({
    label: value,
    value,
  }));

  constructor(
    private modalRef: NzModalRef,
    @Inject(NZ_MODAL_DATA) public data: { key: string; initialData?: any },
    private fb: FormBuilder,
    private http: HttpService,
    private storage: StorageService,
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
    private gtmService: GtmService
  ) {
    this.formModal = this.fb.group({
      date: [data.initialData?.date || null],
      option: [null, Validators.required],
      notes: [data.initialData?.notes || ''],
      underTherapy: [data.initialData?.underTherapy || false],
      specificAllergen: [data.initialData?.specificAllergen || null],
      unit: [data.initialData?.doseMeasureUnit || null],
      format: [data.initialData?.drugFormat || null],
      posology: [data.initialData?.dose || null],
      dose: [data.initialData?.posology || null],
      posologyFrequence: ['Volte'],
      revaccination: [data.initialData?.revaccination || false],
      revaccinationDate: [data.initialData?.revaccinationDate || null],
    });

    if (data.key !== 'drug') {
      this.formModal.get('date')?.setValidators(Validators.required);
    } else {
      this.formModal.get('date')?.clearValidators();
    }
    this.formModal.get('date')?.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.fetchOptions(this.data.key);
    this.formModal.get('option')?.valueChanges.subscribe((selectedCode) => {
      if (selectedCode) {
        this.onOptionSelected(selectedCode);
      }
    });
  }

  toAriaOptions(options: { texts: string[]; values: string[] }): AriaFieldOption[] {
    return options.values.map((value, index) => ({
      value,
      label: options.texts[index] ?? value,
    }));
  }

  private findDrugFormatKey(text: string | undefined): string | null {
    if (!text || !this.drugFormats.texts.length) return null;
    const index = this.drugFormats.texts.indexOf(text);
    if (index !== -1) {
      return this.drugFormats.values[index];
    }
    return null;
  }

  private findOptionValue(type: string | undefined): string | null {
    if (!type || !this.modalOptions.texts.length) return null;
    const index = this.modalOptions.texts.indexOf(type);
    if (index !== -1) {
      return this.modalOptions.values[index];
    }
    return null;
  }

  private findDrugCode(drugType: string | undefined): string | null {
    if (!drugType || !this.httpOptions.length) return null;

    const foundDrug = this.httpOptions.find((drug) => drug.drugName === drugType);
    return foundDrug ? foundDrug.drugCode : null;
  }

  fetchOptions(key: string): void {
    this.http.getMedicalRecordsOptionsByKey(key).subscribe(
      (options) => {
        this.httpOptions = options;
        this.modalOptions = this.normalizeOptions(key, options);
        this.isLoading = false;

        let selectedOptionValue: string | null = null;

        if (key === 'drug') {
          selectedOptionValue = this.findDrugCode(this.data.initialData?.type);
        } else {
          selectedOptionValue = this.findOptionValue(this.data.initialData?.type);
        }

        this.formModal.patchValue({ option: selectedOptionValue });

        if (key === 'allergen' && selectedOptionValue) {
          this.fetchSpecificAllergenItems(selectedOptionValue).then(() => {
            const allergenKey = this.findSpecificAllergenKey(this.data.initialData?.specificAllergen);
            this.formModal.patchValue({ specificAllergen: allergenKey });
          });
        }
        if (key === 'drug' && selectedOptionValue) {
          this.selectedDrugName = this.data.initialData?.type || '';

          this.fetchDrugFormats(this.selectedDrugName).then(() => {
            const drugFormatKey = this.findDrugFormatKey(this.data.initialData?.drugFormat);
            this.formModal.patchValue({ format: drugFormatKey });
          });
        }
        this.formModal.get('option')?.updateValueAndValidity();
      },
      (error) => {
        console.error(`Errore nel caricamento delle opzioni per ${key}:`, error);
        this.isLoading = true;
      }
    );
  }

  onOptionSelected(selectedCode: string): void {
    if (this.data.key === 'pathology' || this.data.key === 'surgical') {
      this.http.getRelatedData(this.data.key, selectedCode).subscribe(
        (response) => {
          this.updateDetails(response);
          this.cdr.detectChanges();
        },
        (error) => {
          console.error('Errore nel recupero dei dati:', error);
        }
      );
    }

    if (this.data.key === 'allergen') {
      this.fetchSpecificAllergenItems(selectedCode);
    }

    if (this.data.key === 'drug') {
      const selectedDrugIndex = this.modalOptions.values.indexOf(selectedCode);
      if (selectedDrugIndex !== -1) {
        this.selectedDrugName = this.httpOptions[selectedDrugIndex]?.drugName || 'Nome non disponibile';

        this.formModal.get('option')?.setValue(selectedCode, { emitEvent: false });

        this.formModal.patchValue({ format: null });

        this.fetchDrugFormats(this.selectedDrugName).then(() => {
          const drugFormatKey = this.findDrugFormatKey(this.data.initialData?.drugFormat);
          this.formModal.patchValue({ format: drugFormatKey });
        });

        this.cdr.detectChanges();
      }
    }
  }

  fetchDrugFormats(drugName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!drugName) {
        resolve();
        return;
      }

      const encodedDrugName = btoa(unescape(encodeURIComponent(drugName)));
      this.http.getDrugFormat(encodedDrugName).subscribe(
        (response) => {
          this.drugFormats = {
            texts: response.map((opt: { description: any }) => opt.description),
            values: response.map((opt: { id: any }) => opt.id),
          };
          this.cdr.detectChanges();
          resolve();
        },
        (error) => {
          console.error('Errore nel caricamento dei formati del farmaco:', error);
          reject(error);
        }
      );
    });
  }

  fetchSpecificAllergenItems(allergenGroupCode: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.getSpecificAllergen(allergenGroupCode).subscribe(
        (response) => {
          this.specificAllergenOptions = {
            texts: response.map((opt: { value: any }) => opt.value),
            values: response.map((opt: { key: any }) => opt.key),
          };

          if (!this.specificAllergenOptions.texts[0].includes(this.data.initialData?.specificAllergen)) {
            this.formModal.patchValue({ specificAllergen: null });
          } else {
            const allergenKey = this.findSpecificAllergenKey(this.data.initialData?.specificAllergen);
            this.formModal.patchValue({ specificAllergen: allergenKey });
          }

          this.cdr.detectChanges();
          resolve();
        },
        (error) => {
          console.error('Errore nel caricamento degli allergeni specifici:', error);
          reject(error);
        }
      );
    });
  }

  private findSpecificAllergenKey(text: string | undefined): string | null {
    if (!text || !this.specificAllergenOptions.texts.length) return null;
    const index = this.specificAllergenOptions.texts.indexOf(text);
    if (index !== -1) {
      return this.specificAllergenOptions.values[index];
    }
    return null;
  }

  updateDetails(response: { type: string; value: string; key?: string }[]): void {
    this.details = {
      gender: response.find((item) => item.type === 'Gender')?.value || '-',
      apparatus: response.find((item) => item.type === 'Apparatus')?.value || '-',
      organ: response.find((item) => item.type === 'Organ')?.value || '-',
      bodyPart: response.find((item) => item.type === 'Body')?.value || '-',
      bodyPartKey: response.find((item) => item.type === 'Body')?.key || '-',
    };
  }

  normalizeOptions(key: string, options: any[]): { texts: string[]; values: string[] } {
    switch (key) {
      case 'pathology':
        return {
          texts: options.map((opt) => opt.value),
          values: options.map((opt) => opt.icdCode),
        };

      case 'vaccination':
        return {
          texts: options.map((opt) => opt.description),
          values: options.map((opt) => opt.code),
        };

      case 'surgical':
        return {
          texts: options.map((opt) => opt.value),
          values: options.map((opt) => opt.icdCode),
        };

      case 'allergen':
        return {
          texts: options.map((opt) => opt.value),
          values: options.map((opt) => opt.icdCode),
        };

      case 'drug':
        return {
          texts: options.map((opt) => opt.translationValues),
          values: options.map((opt) => opt.translationValues),
        };

      default:
        return { texts: [], values: [] };
    }
  }

  save(): void {
    const optionValue = this.formModal.get('option')?.value;
    if (!optionValue || optionValue === '' || this.formModal.invalid) {
      console.log('option value null')
      return;
    }
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData.individualId;
    this.isLoading = true;

    let payload: any;
    let request$: Observable<any>;

    if (this.data.initialData?.id) {
      payload = this.buildUpdatePayload();
      request$ = this.updateRecord(individualId, payload);
    } else {
      payload = this.buildPayload();
      request$ = this.saveRecord(individualId, payload);
    }
    request$.pipe(switchMap(() => this.executeAdditionalCalls(individualId))).subscribe({
      next: () => {
        this.isLoading = false;
        const dataType = this.data.key;
        this.gtmService.pushHealthDataEvent('register_medical_history', dataType);
        this.modalRef.close(payload);
      },
      error: (error) => {
        console.error('Errore durante il salvataggio:', error);
        this.isLoading = false;
      },
    });
  }

  private updateRecord(individualId: string, payload: any): Observable<any> {
    switch (this.data.key) {
      case 'pathology':
        return this.http.updatePathologyRecord(individualId, payload);
      case 'surgical':
        return this.http.updateSurgicalRecord(individualId, payload);
      case 'allergen':
        return this.http.updateAllergenRecord(individualId, payload);
      case 'drug':
        return this.http.updateDrugRecord(individualId, payload);
      case 'vaccination':
        return this.http.updateVaccinationRecord(individualId, payload);
      default:
        return throwError(() => new Error('Tipo di record non valido.'));
    }
  }

  private buildPayload(): any {
    const selectedDate = new Date(this.formModal.value.date);

    const selectedOptionValue = this.formModal.value.option;
    const selectedOptionText = this.modalOptions.texts[this.modalOptions.values.indexOf(selectedOptionValue)] || '';

    const allergenSelectedOptionValue = this.formModal.value.specificAllergen;
    const allergenSelectedOptionText = this.specificAllergenOptions.texts[this.specificAllergenOptions.values.indexOf(allergenSelectedOptionValue)] || '';

    const drugFormatValue = this.formModal.value.format;
    const drugFormatText = this.drugFormats.texts[this.drugFormats.values.indexOf(drugFormatValue)] || '';

    switch (this.data.key) {
      case 'pathology':
        return {
          underTherapy: this.formModal.value.underTherapy,
          diagnosysDate: this.formModal.value.date,
          name: selectedOptionText,
          notes: this.formModal.value.notes,
          ecCodeId: this.formModal.value.option,
          onSetDate: selectedDate,
        };

      case 'surgical':
        return {
          bodyZone: this.details.bodyPart,
          description: selectedOptionText,
          intervention: selectedOptionText,
          ecCodeId: this.formModal.value.option,
          bodyPartECCodeId: this.details.bodyPartKey,
          eventDate: selectedDate,
        };

      case 'allergen':
        return {
          specificAllergen: allergenSelectedOptionText,
          description: selectedOptionText,
          name: selectedOptionText,
          ecCodeId: this.formModal.value.option,
          specificECCodeId: allergenSelectedOptionValue,
          diagnosysDate: this.formModal.value.date,
          onSetDate: selectedDate,
        };

      case 'drug':
        const optionValue = this.httpOptions.find((res) => {
          return res.translationValues == this.formModal.value.option;
        });
        return {
          activeSubstance: btoa(selectedOptionText),
          description: this.selectedDrugName,
          drugType: this.selectedDrugName,
          doseMeasureUnit: this.formModal.value.unit,
          commercialName: btoa(this.selectedDrugName),
          dose: this.formModal.value.posology,
          posology: this.formModal.value.dose,
          posologyFrequence: this.formModal.value.posologyFrequence,
          reminderStartDate: new Date().toISOString(),
          reminderEndDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString(),
          activePrincipleKey: btoa(optionValue ? optionValue.drugCode : this.formModal.value.option),
          drugKey: btoa(optionValue ? optionValue.drugCode : this.formModal.value.option),
          drugFormat: btoa(drugFormatText),
          formatId: String(this.formModal.value.format),
        };

      case 'vaccination':
        return {
          revaccination: this.formModal.value.revaccination,
          vaccinationDate: selectedDate,
          code: this.formModal.value.option,
          description: selectedOptionText,
          CodeId: this.formModal.value.option,
          name: selectedOptionText,
          revaccinationDate: this.formModal.value.revaccinationDate,
        };

      default:
        throw new Error('Tipo di record non valido.');
    }
  }

  private buildUpdatePayload(): any {
    if (!this.data.initialData?.id) {
      console.error('Errore: impossibile aggiornare il record senza ID.');
      return null;
    }

    const selectedDate = new Date(this.formModal.value.date);

    const selectedOptionValue = this.formModal.value.option;
    const selectedOptionText = this.modalOptions.texts[this.modalOptions.values.indexOf(selectedOptionValue)] || '';

    const allergenSelectedOptionValue = this.formModal.value.specificAllergen;
    const allergenSelectedOptionText = this.specificAllergenOptions.texts[this.specificAllergenOptions.values.indexOf(allergenSelectedOptionValue)] || '';

    const drugFormatValue = this.formModal.value.format;
    const drugFormatText = this.drugFormats.texts[this.drugFormats.values.indexOf(drugFormatValue)] || '';

    switch (this.data.key) {
      case 'pathology':
        return {
          id: this.data.initialData.id,
          underTherapy: this.formModal.value.underTherapy,
          diagnosysDate: this.formModal.value.date,
          codeId: null,
          name: selectedOptionText,
          notes: this.formModal.value.notes,
          ecCodeId: this.formModal.value.option,
          onSetDate: selectedDate,
        };

      case 'surgical':
        return {
          id: this.data.initialData.id,
          bodyZone: this.details.bodyPart,
          bodyZoneCode: this.details.bodyPart,
          description: selectedOptionText,
          intervention: selectedOptionText,
          codeId: null,
          ecCodeId: this.formModal.value.option,
          bodyPartECCodeId: this.details.bodyPartKey,
          eventDate: this.formModal.value.date,
          notes: this.formModal.value.notes,
        };

      case 'allergen':
        return {
          id: this.data.initialData.id,
          specificAllergen: allergenSelectedOptionText,
          description: selectedOptionText,
          codeId: null,
          name: selectedOptionText,
          notes: this.formModal.value.notes,
          ecCodeId: this.formModal.value.option,
          specificECCodeId: allergenSelectedOptionValue,
          diagnosysDate: this.formModal.value.date,
          onSetDate: selectedDate,
        };

      case 'drug':
        const optionValue = this.httpOptions.find((res) => {
          res.translationValues === this.formModal.value.option;
        })?.drugCode;
        return {
          id: this.data.initialData.id,
          activeSubstance: btoa(selectedOptionText),
          description: this.selectedDrugName,
          drugType: this.selectedDrugName,
          doseMeasureUnit: this.formModal.value.unit,
          drugTypeCode: this.selectedDrugName,
          commercialName: btoa(this.selectedDrugName),
          dose: this.formModal.value.posology,
          posology: this.formModal.value.dose,
          posologyFrequence: this.formModal.value.posologyFrequence,
          reminderStartDate: new Date().toISOString(),
          reminderEndDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString(),
          activePrincipleKey: btoa(optionValue ? optionValue : this.formModal.value.option),
          drugKey: btoa(optionValue ? optionValue : this.formModal.value.option),
          drugFormat: btoa(drugFormatText),
          formatId: String(this.formModal.value.format),
        };

      case 'vaccination':
        return {
          id: this.data.initialData.id,
          revaccination: this.formModal.value.revaccination,
          vaccinationDate: selectedDate,
          code: this.formModal.value.option,
          description: selectedOptionText,
          codeId: this.formModal.value.option,
          name: selectedOptionText,
          revaccinationDate: this.formModal.value.revaccinationDate,
        };

      default:
        throw new Error('Tipo di record non valido.');
    }
  }

  private saveRecord(individualId: string, payload: any): Observable<any> {
    switch (this.data.key) {
      case 'pathology':
        return this.http.addPathologyRecord(individualId, payload);
      case 'surgical':
        return this.http.addSurgicalRecord(individualId, payload);
      case 'allergen':
        return this.http.addAllergenRecord(individualId, payload);
      case 'drug':
        return this.http.addDrugRecord(individualId, payload);
      case 'vaccination':
        return this.http.addVaccinationRecord(individualId, payload);
      default:
        return throwError(() => new Error('Tipo di record non valido.'));
    }
  }

  private executeAdditionalCalls(individualId: string): Observable<any> {
    this.dataService.companyId$.subscribe((companyId) => {
      this.companyId = companyId;
    });
    const eventLogPayload = {
      companyId: this.companyId!,
      individualId: individualId,
      userId: individualId,
      grantTo: '',
      eventType: 'Modifica Cartella Clinica',
      eventValue: 'Aggiunto nuovo record',
      eventTypeEnum: 1,
      createdOn: new Date().toISOString(),
      createdBy: individualId,
    };

    const deleteSchedulePayload = {
      individualId,
      instanceCode: '', //non so che passare
      serviceType: 'MedicalDataIncomplete', //non so se passare sempre questo o ci sono altri valori disponibili
    };

    return forkJoin([this.http.deleteNotificationSchedule(deleteSchedulePayload), this.http.getMedicalRecordMenuInfo(individualId)]);
  }

  close(): void {
    this.modalRef.close(null);
  }

  leaveYears(date: Date) {
    return subYears(date, 50);
  }

  addYears(date: Date) {
    return addYears(date, 50);
  }

  addDays(date: Date) {
    return addDays(date, 1);
  }
}
